/** @jsx isolateComponent */
import { css } from '@emotion/core';
import { Colors } from '@zapier/design-system';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import type { UniversalNavLinkType } from '../../../constants';
import { SubMenuHeading } from './SubMenuHeading';
import { SubMenuList } from './SubMenuList';
import { ICON_SIZE, HEADING_GAP } from './constants';

type Props = {
  /**
   * Top-level headings
   * along with an optional description or list of links
   */
  links: UniversalNavLinkType[];
  /**
   * Function called when closing menu on a link from within the MegaMenu
   */
  onCloseMenu?: () => void;
};

const Styles = labelStyles('MegaMenuSubmenu', {
  root: css({
    color: Colors.earth,
    display: 'grid',
    gap: '4%',
    gridTemplateColumns: 'repeat(3, minmax(0px, max-content))',
    gridTemplateRows: 'auto',
    height: '100%',
  }),
  group: css({
    color: Colors.inherit,
    '&:hover, &:focus, &:focus-within': {
      color: Colors.zapier,
    },
  }),
  listWrapper: (hasIcon: boolean) =>
    css({
      paddingLeft: hasIcon ? `${ICON_SIZE + HEADING_GAP}px` : 0,
    }),
});

/**
 * @private
 * Group of links inside the MegaMenu
 */
export const MegaMenuSubmenu = (props: Props) => {
  const { links } = props;

  return (
    <ul css={Styles.root}>
      {links.map((link) => (
        <li key={link.text} css={Styles.group}>
          <div role={link.submenu ? 'group' : 'presentation'}>
            <SubMenuHeading link={link} onCloseMenu={props.onCloseMenu} />
            {link.submenu && (
              <div css={Styles.listWrapper(!!link.icon)}>
                <SubMenuList
                  submenu={link.submenu}
                  onCloseMenu={props.onCloseMenu}
                />
              </div>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
};
